import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { CountdownComponent, CountdownEvent } from 'ngx-countdown';
import { finalize } from 'rxjs/operators';
import * as SockJS from 'sockjs-client';
import { Global } from 'src/app/global/global';
import { ChatMessage } from 'src/app/models/chat-message.model';
import { ChatNotification } from 'src/app/models/chat-notification.model';
import { Friend } from 'src/app/models/friend.model';
import { User } from 'src/app/models/user.model';
import { AlertService } from 'src/app/service/alert.service';
import { DisordersTranslationService } from 'src/app/service/disorders-translation.service';
import { FriendService } from 'src/app/service/http-services/friend.service';
import { MessagesService } from 'src/app/service/http-services/messages.service';
import { UserService } from 'src/app/service/http-services/user.service';
import { TabNotificationService } from 'src/app/service/tab-notification.service';
import { Client, Message, over } from 'stompjs';
import { ConversationStarterComponent } from '../conversation-starter/conversation-starter.component';
import { HumanizeDurationLanguage, HumanizeDuration } from 'humanize-duration-ts';
import { getLocaleId } from '@angular/common';
import getUserLocale from 'get-user-locale';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.css']
})
export class ChatComponent implements OnInit, OnDestroy, AfterViewChecked {

  @ViewChild('scroll') private scrollBottom!: ElementRef;
  @ViewChild(ConversationStarterComponent) private convStarter!: ConversationStarterComponent

  //chat
  conn!: Client;
  form: FormGroup;
  messages: ChatMessage[] = [];
  chatFriend!: Friend;
  showEmoji: boolean = false;
  disableScroll: boolean = false;
  isMessagesLodaing: boolean = true;


  //global
  myUser: User;
  isComponentLoading: boolean = false;

  //friends
  friends: Friend[] = []
  showNoFriendsMessage: boolean = false;
  findNewFriendLoading: boolean = false;
  newFriendTimeout: number = 0;
  isNewFriendTimeout: boolean = false;
  showDeleteFriendPopup: boolean = false;
  showFriends: boolean = true;
  animationFriends: boolean = true;
  friendNameFilter!: string;
  humanizer: HumanizeDuration = new HumanizeDuration(new HumanizeDurationLanguage());

  @ViewChild('cd', { static: false }) private countdown!: CountdownComponent;

  constructor(
    public fb: FormBuilder,
    private messageService: MessagesService,
    private friendService: FriendService,
    private alertService: AlertService,
    private disordersTranslationService: DisordersTranslationService,
    private tabNotification: TabNotificationService,
    userService: UserService
  ) {
    this.form = fb.group({
      message: [null, [Validators.required, Validators.minLength(1)]]
    })
    this.myUser = userService.getUserStorage();
  }

  ngOnInit() {
    this.isComponentLoading = true;
    this.loadFriends().subscribe(rsp => {
      this.friends = rsp;
      if (this.friends.length > 0) {
        this.setChatFriend(this.friends[0]);
        this.loadMessages().subscribe(rsp => {
          this.messages = rsp;
          this.loadNewMessagesCount();
          this.isComponentLoading = false;
        })
      }
      else {
        this.showNoFriendsMessage = true;
        this.isComponentLoading = false;
      }
      this.connect()
    });
  }

  ngOnDestroy() {
    this.conn.disconnect(() => {
      console.log("disconnected")
    }, {})
  }

  ngAfterViewChecked() {
    this.scrollDownChat();
  }

  connect() {
    this.conn = over(new SockJS(Global.BASE_API_URL + '/ws'));
    this.conn.debug = () => { };
    const _this = this;
    this.conn.connect({ user: this.myUser.id }, () => {
      _this.conn.subscribe("/user/" + this.myUser.id + "/chat/messages", notification => {
        this.onChatNotificationReceived(notification)
      })
    })
  }

  loadMessages() {
    return this.messageService.getMessages(this.chatFriend.id)
  }

  loadNewMessagesCount() {
    this.friends.forEach(f => {
      this.messageService.countMessages(f.id).subscribe(count => {
        f.newMessages = count;
      })
    })
  }

  loadFriends() {
    return this.friendService.getFriendsList();
  }

  sendMessage() {
    if (this.form.valid) {
      let chatMessage: ChatMessage = {
        senderId: this.myUser.id,
        recipientId: this.chatFriend.id,
        senderName: this.myUser.name,
        recipientName: this.chatFriend.name,
        content: this.form.controls.message.value,
        timestamp: new Date()
      };
      console.log(chatMessage);
      this.conn.send("/app/chat", {}, JSON.stringify(chatMessage))
      this.messages.push(chatMessage);
    }
    this.form.controls.message.reset();
    this.textarea.focus();
    this.showEmoji = false;
    this.textarea.rows = 1;
    this.moveFriendToTop(this.chatFriend.id);
  }

  startConversation() {
    let chatMessage: ChatMessage = {
      senderId: this.myUser.id,
      recipientId: this.chatFriend.id,
      senderName: "[" + this.myUser.name + " started chat with conversation starter]",
      recipientName: this.chatFriend.name,
      content: this.convStarter.question,
      timestamp: new Date()
    };
    this.conn.send("/app/chat", {}, JSON.stringify(chatMessage))
    this.messages.push(chatMessage);
    this.form.controls.message.reset();
    this.textarea.focus();
    this.moveFriendToTop(this.chatFriend.id);
  }

  onChatNotificationReceived(notification: Message) {
    let notificationObj = JSON.parse(notification.body);
    let chatNotification: ChatNotification = new ChatNotification(notificationObj.id, notificationObj.senderId, notificationObj.senderName);
    if (chatNotification.senderId === (this.chatFriend.id)) {
      this.messageService.getMessage(chatNotification.id).subscribe(msg => {
        this.messages.push(msg);
      })
    }
    else {
      this.messageService.countMessages(chatNotification.senderId).subscribe(newCount => {
        this.friends.filter(x => x.id === chatNotification.senderId)
          .forEach(f => f.newMessages = newCount)
      })
    }
    this.moveFriendToTop(chatNotification.senderId);
    this.tabNotification.blink("New message!")
  }

  scrollDownChat() {
    if (this.scrollBottom) {
      if (!this.disableScroll) {
        this.scrollBottom.nativeElement.scrollTop = this.scrollBottom.nativeElement.scrollHeight;
      }
    }
  }

  onScroll() {
    let element = this.scrollBottom.nativeElement
    let atBottom = element.scrollHeight - element.scrollTop === element.clientHeight
    if (atBottom) {
      this.disableScroll = false
    } else {
      this.disableScroll = true
    }
  }

  setChatFriend(newChatFriend: Friend) {
    this.isMessagesLodaing = true;
    this.messages = [];
    this.chatFriend = newChatFriend;
    this.friends.filter(x => x.id === newChatFriend.id).forEach(x => x.newMessages = 0)
    this.friendNameFilter = "";
    this.loadMessages().subscribe(rsp => {
      this.messages = rsp;
      this.isMessagesLodaing = false;
    });
  }

  formatDate(message: ChatMessage) {
    let parsedDate: Date = new Date(message.timestamp);
    let isToday = parsedDate.getDate() === new Date().getDate();
    if (isToday) {
      return parsedDate.toLocaleTimeString(getUserLocale(), { hour: '2-digit', minute: '2-digit' })
    }
    else {
      return parsedDate.toLocaleDateString(getUserLocale());
    }
  }

  isMyMessage(message: ChatMessage): boolean {
    return message.senderId === this.myUser.id;
  }

  getTranslatedDisorders(friend: Friend) {
    return this.disordersTranslationService.translateDisordersEnum(friend.disorders);
  }


  findFriend() {
    this.findNewFriendLoading = true;
    this.friendService.findFriend().pipe(
      finalize(() => this.findNewFriendLoading = false)
    )
      .subscribe(message => {
        this.friends.push(message);
        this.setChatFriend(message);
        this.moveFriendToTop(message.id);
        this.showNoFriendsMessage = false;
      },
        err => {
          this.handleTooManyRequestsError(err);
          this.alertService.error(err.error.message, { autoClose: true })
        })
  }

  getMessageAvatar(message: ChatMessage) {
    if (this.isMyMessage(message)) {
      return this.getAvatar(this.myUser.avatarId);
    }
    else {
      return this.getAvatar(this.chatFriend.avatarId);
    }
  }

  getAvatar(id: string): string {
    const baseUrl = "https://getbetter-avatars.s3-eu-west-1.amazonaws.com/";
    if (id !== null) {
      return baseUrl + id
    }
    else {
      return "https://i1.wp.com/www.mnleadership.org/wp-content/uploads/2017/02/Anonymous-Avatar.png?ssl=1"
    }
  }


  handleTooManyRequestsError(err: any) {
    this.newFriendTimeout = err.error.secondsToRefresh;
    this.isNewFriendTimeout = true;
  }

  onTimerFinished(e: CountdownEvent) {
    if (e.action == 'done') {
      this.isNewFriendTimeout = false;
    }
  }

  textareaOverflow() {
    if (this.textarea.clientHeight < this.textarea.scrollHeight) {
      this.textarea.rows = this.textarea.rows + 1;
      window.scrollTo(0, document.body.scrollHeight);
    }
  }

  addEmoji(emoji: any) {
    let text = this.messageForm.value;
    if (text !== null) {
      this.messageForm.setValue(text + emoji.emoji.native);
    }
    else {
      this.messageForm.setValue(emoji.emoji.native);
    }
  }

  toggleEmoji() {
    this.showEmoji = !this.showEmoji;
  }

  unfriend() {
    this.friendService.deleteFriend(this.chatFriend.id).subscribe(rsp => {
      this.friends = this.friends.filter(x => x.id !== this.chatFriend.id)
      if (this.friends.length === 0) {
        this.showNoFriendsMessage = true;
        this.chatFriend = this.friends[0]
      }
      else {
        this.setChatFriend(this.friends[0])
      }
    })
  }

  moveFriendToTop(friendId: string) {
    let f: Friend = this.friends.find(x => x.id === friendId)!;
    this.friends.splice(this.friends.indexOf(f), 1);
    this.friends.unshift(f);
  }

  showHideFriends() {
    if (this.showFriends) {
      this.animationFriends = false;
      setTimeout(() => {
        this.showFriends = false
      }, 700)
    }
    else {
      this.showFriends = true;
      this.animationFriends = true;
    }
  }

  getTotalNewMessages(): number {
    return this.friends.map(x => x.newMessages).reduce((a, b) => a + b, 0);
  }

  onlineStatus(friend: Friend): Map<string, string> {
    if (friend.lastLogin == null) {
      return new Map([["status", "Offline"],
      ["lastOnline", "unknown"]])
    }
    var diff = moment(new Date()).diff(friend.lastLogin, "minutes");
    var status;
    if (diff < 10) {
      status = "Online"
    }
    else if (diff < 60) {
      status = "Away"
    }
    else {
      status = "Offline"
    }
    return new Map([["status", status],
    ["lastOnline", this.humanizer.humanize(diff * 60000).toString()]])
  }

  indicatorColor(friend: Friend): string {
    var status = this.onlineStatus(friend).get("status");
    if (status === "Online") {
      return "rgb(49, 162, 76)"
    }
    else if (status === "Away") {
      return "#d7d71d";
    }
    else if (status === "Offline") {
      return "rgb(208, 208, 188)";
    }
    else return ""
  }

  getLastLogin(friend: Friend) {
    if (friend.lastLogin == null) {
      return ""
    }
    return moment(new Date()).diff(friend.lastLogin, "minutes");
  }

  get textarea() {
    return document.getElementsByTagName("textarea")[0]
  }

  get messageForm() {
    return this.form.controls.message;
  }


}
